import { Box } from "@mui/material";
import { useContext, useEffect, useRef, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { httpAPI_INQ003, httpAPI_INQ015 } from "./api/request";
import { GlobalContext } from "../../context/globalContext";
import TkTitle from "../../components/atoms/TkTitle";
import "./_style.scss";
import { TkGetCode } from "../../utils/TkGetCode";
import {
  FacilityKind,
  InquiryCode,
  Prefectures,
  YesOrNoFlug,
} from "../../assets/data/Constants";
import TkButton from "../../components/atoms/TkButton";
import TkBackButton from "../../components/molecules/TkBackButton";
import { BusinessContext } from "../../context/businessContext";
import TkAccordion from "../../components/atoms/TkAccordion";
import TkInquiryTable from "./components/components";
import {
  accordionContents,
  commentHeader,
  headList,
  inquiryList,
  staffInfoList,
} from "./parts";
import { IAPI_INQ003_Response } from "../../type/apiType";
import TkTable from "../../components/atoms/TkTable";
import { changeZipcodeFormat } from "../../utils/useTools";
import TkTextArea from "../../components/molecules/TkTextArea";
import TkMessageDialog from "../../components/molecules/TkMessageDialog";
import _ from "lodash";

/**
 * SCRW120004 施設照会
 * @return SCRW120004 施設照会
 */
const SCRW120004 = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { state, setState } = useContext(GlobalContext);
  const { state: businessState, setState: setBusinessState } =
    useContext(BusinessContext);
  const [initData, setInitData] = useState<IAPI_INQ003_Response>({});
  const [facilityInfoList, setFacilityInfoList] = useState(
    _.cloneDeep(inquiryList)
  );
  const [historyList, setHistoryList] = useState<any>([]);
  const [accordionItem, setAccordionItem] = useState<any>();
  const [staffInfo, setStaffInfo] = useState<any>([]);
  const [comment, setComment] = useState("");
  const [open, setOpen] = useState(false);
  const accordionShowBoolRef = useRef<boolean>(true);

  useEffect(() => {
    openingScreen();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 初期表示
  async function openingScreen() {
    // 施設照会参照API 呼び出し
    try {
      const res = await httpAPI_INQ003({
        contractorNo:
          businessState.job_114?.contractorNo ??
          businessState.common?.contractorNo,
        facilityNo:
          businessState.job_114?.facilityNo ?? location.state?.facilityNo,
      });

      if (res.resultCd === "I0000") {
        // 施設情報
        let resArr = facilityInfoList;
        for (let i in res) {
          resArr.forEach((item) => {
            let targetObj = item.find((v) => v.keyword === i)!;

            if (targetObj) {
              if (i === "contractorNo" || i === "corpName") {
                targetObj.fill = res.corpApplyKbn === "0";
              }
              if (i === "facilityNo" || i === "facilityName") {
                targetObj.fill = res.facilityClassName === "社会福祉施設等";
              }
              if (i === "facilityName") {
                targetObj.elderly =
                  res.facilityClassName === "特定介護保険施設等（介護）";
                targetObj.accessible =
                  res.facilityClassName === "特定介護保険施設等（障害）";
              }
              targetObj.text = res[i as keyof typeof res];
            }
          });
        }
        resArr.forEach((item) => {
          let address = item.find((i) => i.keyword === "address")!;
          if (address) {
            address.text = (
              <>
                {"〒" + (changeZipcodeFormat(res.zipcode) ?? "")}
                <br />
                {(TkGetCode(Prefectures, res.prefectureCd) ?? "") +
                  (res.addressCity ?? "") +
                  (res.addressOthers ?? "")}
              </>
            );
          }
        });
        setFacilityInfoList([...resArr]);

        // 廃止・休止・解除・非加入情報
        let accArr = accordionContents;
        let hasValue: any = [];
        for (let i in res) {
          accArr.forEach((item) => {
            let targetObj = item.find((v) => v.keyword === i)!;

            if (targetObj) {
              hasValue.push(true);
              if (
                i === "notJoinAfterReformFlg" ||
                i === "nonParticipationAfterFacilityConversion"
              ) {
                targetObj.text = TkGetCode(
                  YesOrNoFlug,
                  res[i as keyof typeof res]
                );
              } else {
                targetObj.text = res[i as keyof typeof res];
              }
            } else {
              hasValue.push(false);
            }
          });
        }

        if (
          !res?.abolitionDate &&
          !res?.closedDate &&
          !res?.calcelDate &&
          !res?.notJoinAfterReformCancelDate &&
          !res?.nonParticipationAfterFacilityConversionCancelDate &&
          res?.notJoinAfterReformFlg === "0" &&
          res?.nonParticipationAfterFacilityConversion === "0"
        ) {
          accordionShowBoolRef.current = false;
        }

        // すべての項目が空の場合は非表示
        let dontShow = hasValue.every((v: any) => v === false);

        const accordionItem = !dontShow ? (
          <>
            <Box className="tkLineTitle">{"廃止・休止・解除・非加入情報"}</Box>
            <TkInquiryTable list={accArr} />
          </>
        ) : (
          ""
        );
        setAccordionItem(accordionItem);

        // 職員状況
        let stfArr = staffInfoList;
        stfArr.forEach((item) => {
          // 4/1職員数
          let premiumStaffNum = item.find(
            (v) => v.keyword === "premiumStaffNum"
          )!;
          if (premiumStaffNum) {
            premiumStaffNum.text =
              res.facilityClassName === "社会福祉施設等"
                ? (res.premiumStaffNum ?? "-") + "人"
                : (res.premiumStaffNum ?? "-") +
                  "人(うち補助対象:" +
                  (res.unitPremiumStaffNum ?? "-") +
                  "人)";
          }
          // 現在職員数
          let totalStaffNum = item.find((v) => v.keyword === "totalStaffNum")!;
          if (totalStaffNum) {
            totalStaffNum.text =
              res.facilityClassName === "社会福祉施設等"
                ? (res.totalStaffNum ?? "-") + "人"
                : (res.totalStaffNum ?? "-") +
                  "人(うち補助対象:" +
                  (res.totalStaffNumAuxiliary ?? "-") +
                  "人)";
          }
        });
        setStaffInfo(stfArr);

        // 施設情報変更履歴
        const historyList = res.facilityHistoryList?.map((item) => [
          {
            children: !!item.nameChangeDate
              ? item.nameChangeDate
              : !!item.addressChangeDate
              ? item.addressChangeDate
              : item.kindChangeDate,
          },
          {
            children: item.nameChangeDate
              ? "施設の名称"
              : item.addressChangeDate
              ? "施設の所在地"
              : "施設の種類",
          },
          {
            children: item.nameChangeDate ? (
              item.bFacilityName
            ) : item.addressChangeDate ? (
              <>
                {"〒" + (changeZipcodeFormat(item.bZipcode) ?? "")}
                <br />
                {(TkGetCode(Prefectures, item.bPrefectureCd) ?? "") +
                  (item.bAddressCity ?? "") +
                  (item.bAddressOthers ?? "")}
              </>
            ) : (
              FacilityKind[item.bFacilityKind as keyof typeof FacilityKind]
            ),
          },
          {
            children: item.nameChangeDate ? (
              item.aFacilityName
            ) : item.addressChangeDate ? (
              <>
                {"〒" + (changeZipcodeFormat(item.aZipcode) ?? "")}
                <br />
                {(TkGetCode(Prefectures, item.aPrefectureCd) ?? "") +
                  (item.aAddressCity ?? "") +
                  (item.aAddressOthers ?? "")}
              </>
            ) : (
              FacilityKind[item.aFacilityKind as keyof typeof FacilityKind]
            ),
          },
        ]);
        setHistoryList(historyList);

        setInitData(res);

        setState({
          ...state,
          errorMsgId: "",
        });
      } else {
        setState({ ...state, errorMsgId: res.resultCd });
      }
    } catch (error) {
      navigate("/error");
    }
  }

  const handleUpdateClick = () => {
    if (!comment.match(/^([^ -~｡-ﾟ])*$/)) return;

    setOpen(true);
  };

  const handleCommentUpdate = async () => {
    // 照会用コメント更新API 呼び出し
    try {
      const res = await httpAPI_INQ015({
        contractorNo: initData.contractorNo,
        facilityNo: initData.facilityNo,
        inquiryCode: InquiryCode["施設"],
        refComment: comment,
      });

      if (res.resultCd === "I0000") {
        setState({
          ...state,
          errorMsgId: "",
        });
      } else {
        setState({
          ...state,
          errorMsgId: res.resultCd,
        });
      }
    } catch (error) {
      navigate("/error");
    } finally {
      setOpen(false);
    }
  };

  const contractorInfo = () => {
    navigate("/SCRC040101", { state: { contractorNo: initData.contractorNo } });
  };

  const staffList = () => {
    setBusinessState({
      ...businessState,
      job_114: {
        ...businessState.job_114,
        searchContractorNo: initData.contractorNo,
        facilityNo: initData.facilityNo,
        searchResultDisplayTarget: "3",
        detailDisplayCd: "3",
      },
    });
    navigate("/SCRW120002");
  };

  return (
    <Box data-testid="SCRW120004PageForTest" className="tkSCRW120004">
      <Box className="tkTitleCenter">
        <TkTitle children={"施設照会"} size="large" />
      </Box>

      <Box className="tkLineTitle">
        施設情報【{initData.facilityClassName ?? ""}】
      </Box>
      <TkInquiryTable list={facilityInfoList} />

      <Box className="comments">
        <Box className="tkLineTitle">{"追記情報"}</Box>
        <TkTable
          headList={commentHeader}
          bodyList={[
            [
              {
                children: (
                  <TkTextArea
                    onlyFullWidth
                    inputProps={{ maxLength: 2000 }}
                    wantToSetValue={initData.refComment}
                    onChange={(e: any) => setComment(e.target.value)}
                  />
                ),
              },
            ],
          ]}
        />
        <Box className="button">
          <TkButton onClick={handleUpdateClick}>コメント更新</TkButton>
        </Box>
      </Box>

      {accordionShowBoolRef.current && (
        <TkAccordion
          title="廃止・休止・解除・非加入情報"
          titleClassName="tkLineTitle"
          children={accordionItem}
        />
      )}

      <Box className="tkLineTitle">{"職員状況"}</Box>
      <TkInquiryTable list={staffInfo} />

      <Box className="tkLineTitle">{"施設情報変更履歴"}</Box>
      <TkTable headList={headList} bodyList={historyList} />

      <Box className="tkBtnArea">
        <TkBackButton />
        <TkButton onClick={contractorInfo}>{"契約者情報"}</TkButton>
        <TkButton onClick={staffList}>{"職員一覧"}</TkButton>
      </Box>

      <TkMessageDialog
        open={open}
        onOky={handleCommentUpdate}
        onCancel={() => setOpen(false)}
        title="更新"
        contextText="　コメントを更新しますが、問題ないでしょうか。"
        cancelText="いいえ"
        okText="はい"
      />
    </Box>
  );
};

export default SCRW120004;
